import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { AppInjector } from 'app/app.module';
import { PdfBlock } from 'app/interfaces/pdfblock';
import { RecipientRole } from 'app/main/pages/edit-template/edit-template.dto';
import { EventHandlersService } from 'app/main/pages/edit-template/event-handler.service';
import { HelperService } from 'app/main/pages/edit-template/helper.service';
import { AddNewComponent } from 'app/main/pages/general-modals/add-new/add-new.component';
import { RecipientfieldsService } from 'app/main/pages/settings/recipientfields/recipientfields.service';
import { AuthFacade } from 'app/store/auth.facade';
import { CrmData } from 'app/store/reducers/auth.reducer';
import { ToastrService } from 'ngx-toastr';
import { Router } from '@angular/router';
import { filter, interval, startWith, switchMap, take, BehaviorSubject, expand, delay, of, skip, timeout } from 'rxjs';
import uuid from 'short-unique-id';
import { GlobalService } from './global.service';
import { Deal } from './integrations/integration.dto';
import { IntegrationService } from './integrations/integrations.service';
import { PipeDriveService } from './integrations/pipedrive/pipe-drive.service';
import { MAX_DOCUMENT_UPLOAD_SIZE_BYTES, splitFilesBySizeLimit } from 'app/constants/upload-limits';
import { FileSizePipe } from 'app/pipes/file-size.pipe';

interface PDFTOIMAGE {
	    original : string
}

interface UploadDocumentTaskResponse {
  task_id: string;
  images?: PDFTOIMAGE[];
}

interface UploadDocumentStatusResponse {
  status: string;
  images?: PDFTOIMAGE[];
}

type UploadDocumentTerminalStatus = 'complete' | 'completed' | 'fail' | 'failed';

/** Matches PdfToImageJob::$timeout on the backend (15 minutes). */
const UPLOAD_DOCUMENT_POLL_TIMEOUT_MS = 15 * 60 * 1000;

export interface Headers {}



@Injectable({
  providedIn: 'root'
})

export class UploadsService {
  private uuid = new uuid({ length: 6, dictionary: 'number' });
  public uploadingProgress: number = 0;
  private renderer: Renderer2;
  public loading$ = new BehaviorSubject<boolean>(false);
  private pollSubscription: { [taskId: string]: any } = {};
  private readonly fileSizePipe = new FileSizePipe();
  public httpOptions = {
    headers: new HttpHeaders({
      "Content-Type": "multipart/form-data"
    })
  };
  constructor(
    private http: HttpClient,
    public toastr: ToastrService,
    private globalService: GlobalService,
    private addNewComponent: AddNewComponent,
    private _translocoService: TranslocoService,
    private recipientfieldsService: RecipientfieldsService,
	    private _integrationService$: IntegrationService,
	    private _authFacade$: AuthFacade,
	    private _helperService$: HelperService,
      private router: Router,
	    rendererFactory: RendererFactory2,
	    private _pipeDriveService$: PipeDriveService, 
	  ) { this.renderer = rendererFactory.createRenderer(null, null); }



  /**
   * upload the file in the Google Cloud
   * @param formData 
   */
  uploadFile(formData: FormData) {
    return this.http.post('upload-files', formData);
  }

  /**
   * delete the attachment block folder
   * @param formData 
   */
  deleteFolder(formData){
    return this.http.delete('upload-files/1', { body: formData });
  }



	  /** Upload Document From Dashboard Section Or From Navbar */
	  uploadDocument(formData: FormData){
	    return this.http.post(`dashboard/pdf-to-image`, formData);
	  }


  
	  trackUploadedDocumentStatus(taskId: number | string) {
	    return this.http.get<UploadDocumentStatusResponse>(`dashboard/pdf-to-image/${taskId}/status`);
	  }


  /**
   * Convert PDF to HTML 
   * @param formData
   */
  pdfToHtml(formData: FormData){
    return this.http.post(`dashboard/pdf-to-html`, formData);
  }
  



  /**
   * collect the form data from input file
   * @param files 
   */
  collectFormData(files, key:string="files[]") {
    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append(key, files[i]);
    }
    return formData;
  }





	  uploadFiles(files: File[]): void {
	    const { accepted, rejected } = splitFilesBySizeLimit(files);
	    rejected.forEach(file => this.toastr.error(
	      this._translocoService.translate('Toaster message for upload document (Dashboard upload document widget) - File exceeds the max size limit', {
	        filename: file.name,
	        maxSize: this.fileSizePipe.transform(MAX_DOCUMENT_UPLOAD_SIZE_BYTES)
	      }),
	      'Error'
	    ));
	    if (accepted.length === 0) return;
	    files = accepted;
	    this.loading$.next(true);
	    this.triggerToaster();
	    const formData = this.collectFormData(files, 'file');
	    this.uploadDocument(formData).subscribe({
	      next: ({ task_id }: UploadDocumentTaskResponse) => {
          if (!task_id) {
            this.toastr.clear();
            this.toastr.error(this._translocoService.translate('File upload error (upload files) - File upload failed'), 'Error');
            return;
          }

          this.pollUploadedDocumentStatus(task_id, files);
	      },
	      error: () => {
          this.loading$.next(false);
	        this.toastr.clear();
	        this.toastr.error(this._translocoService.translate('File upload error (upload files) - File upload failed'), 'Error');
	      }
	    });
	  }

  private pollUploadedDocumentStatus(taskId: string, files: File[]): void {
    // Clean up any existing subscription for this task
    if (this.pollSubscription[taskId]) {
      this.pollSubscription[taskId].unsubscribe();
    }

    this.pollSubscription[taskId] = of(null).pipe(
      expand(() =>
        this.trackUploadedDocumentStatus(taskId).pipe(
          delay(3000)
        )
      ),
      skip(1),
      timeout(UPLOAD_DOCUMENT_POLL_TIMEOUT_MS),
      filter(({ status }: UploadDocumentStatusResponse) => ['complete', 'completed', 'fail', 'failed'].includes(status)),
      take(1)
    ).subscribe({
      next: async ({ status, images }: UploadDocumentStatusResponse) => {
        if (status === 'fail' || status === 'failed') {
          this.toastr.clear();
          this.toastr.error(this._translocoService.translate('File upload error (upload files) - File upload failed'), 'Error');
          this.loading$.next(false);
          return;
        }

        const pdfToImage = images ?? [];
        if (Array.isArray(pdfToImage) && pdfToImage.length > 0) {
          const eventHandlersService = AppInjector.get(EventHandlersService);
          const pdfBlockData = PdfBlock.block({ uuid: this.uuid(), is_parent: 1 });
          const { block, pdfBlock } = eventHandlersService.generatePdfBlocks(
            pdfToImage,
            0,
            '',
            pdfBlockData.uuid,
            pdfBlockData,
            false
          ) as { block: any; pdfBlock: string };

          block[pdfBlockData.uuid] = { ...pdfBlockData, ...{ files: pdfToImage } };
          const formData = {
            name: files[0]?.name,
            content: pdfBlock,
            blocks: block
          };

          if (await this._integrationService$.setCrmLayout()) {
            this.performCRMAction(formData);
          } else {
            this.fetchRecipientRoles(formData);
          }

          this.loading$.next(false);
          delete this.pollSubscription[taskId];
          return;
        }

        this.toastr.clear();
        this.toastr.error(this._translocoService.translate('File upload error (upload files) - File upload failed'), 'Error');
        this.loading$.next(false);
        delete this.pollSubscription[taskId];
      },
      error: () => {
        this.toastr.clear();
        this.toastr.error(this._translocoService.translate('File upload error (upload files) - File upload failed'), 'Error');
        this.loading$.next(false);
        delete this.pollSubscription[taskId];
      }
    });
  }


  performCRMAction(formData) {
    this._authFacade$.crmData$.pipe(take(1)).subscribe({
      next: (crmData: CrmData) => {
        const dealData = this._integrationService$.getCrmParams(crmData);
        this._pipeDriveService$.getDealDetail(dealData).subscribe({
          next: async (response: Deal) => {
            const recipientsData = response?.data?.contacts_data ?? [];
            const roles: RecipientRole[] = [];
            for (const person of recipientsData) {
              const role = this._helperService$.addNewRole(
                "id",
                null,
                roles.length + 1,
                true,
                person
              );
              roles.push(role);
            }
            this.createNewDocument(roles, formData);
          }
        })
      }
    })
  }



  protected fetchRecipientRoles(formData): void {
    this.recipientfieldsService.fetchRecipientFields().subscribe({
      next : (roles: RecipientRole[]) => this.createNewDocument(roles, formData),
      error : (error) => this.globalService.toastrError(error)
    });
  }




  protected createNewDocument(roles: RecipientRole[], formData){
    const template_roles = roles || [];
    this.addNewComponent.initAddNewForm(false, formData, template_roles);
    this.addNewComponent.addNew();
    this.toastr.clear();
  }


  
  triggerToaster() {
    this.toastr.toastrConfig.preventDuplicates = true;
    this.toastr.success(this._translocoService.translate('File uploading toast (document upload files) - File uploading'), 'Success', {
      toastClass: 'toast ngx-toastr',
      closeButton: true,
      progressBar: true,
      progressAnimation: 'increasing'
    });
  }
}
